import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ComponentProps, type ComponentType } from 'react'
import { useIntl } from 'react-intl'

import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  isFlightSavedItemData,
  isLeFlightSavedItemData,
  type TripStop,
} from 'tripPlanner/api/types'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'
import { useTripListPricingContext } from '../contexts/TripListPricingContext'
import { useFetchCartPanelOffers } from '../hooks/useFetchCartPanelOffers'
import { useCartPanelItems } from '../hooks/useCartPanelItems'
import { useTripChatCheckoutActions } from '../hooks/useTripChatCheckoutActions'
import { useTripListNoteUpdate } from '../hooks/useTripListNoteUpdate'
import { useTripListStatusUpdate } from '../hooks/useTripListStatusUpdate'
import { rememberTripChatCheckoutReturnView } from '../hooks/useRightPanelState'
import { isCartPanelItemCheckoutReady } from '../utils/cartPanelUtils'
import {
  buildTripListRows,
  filterTripListRows,
  groupTripListRows,
  sortTripListRows,
} from '../components/ListView/tripListRows'
import {
  applyCartPanelPricesToTripListRows,
  getTripListRowTripItemId,
  isTripListRowMatchingCartPanelItem,
} from '../components/ListView/tripListCartPrices'
import { canMoveTripListRowToAnotherDay, type TripListMoveDayOption } from '../components/ListView/tripListMoveDayOptions'
import {
  getSelectedTripListRows,
  getTripListRowAskAiContext,
  getTripListRowsAskAiContext,
  getTripListRowsAskAiTitle,
  getTripListRowsMoveAskAiContext,
  getTripListRowsMoveAskAiPlaceholder,
  getTripListRowsMoveAskAiTitle,
  getTripListRowsTripItemIds,
} from '../components/ListView/tripListSelection'
import type {
  TripListFilters,
  TripListRow,
  TripListTransportRow,
  TripListRowType,
  TripListSort,
  TripListSortField,
  TripListStatusValue,
} from '../components/ListView/tripListTypes'
import TripListView from '../components/ListView/TripListView'
import ConfirmDeleteTripListItemsModal from '../components/ListView/ConfirmDeleteTripListItemsModal'
import FlightSearchDrawer from '../components/FlightSearchDrawer/FlightSearchDrawer'
import { isBookedFlightOption, parseSearchParams } from '../components/ShortlistedFlightsSection/flightShortlistUtils'
import useBookShortlistedFlight from '../components/ShortlistedFlightsSection/useBookShortlistedFlight'
import useRefreshShortlistedFlights from '../components/ShortlistedFlightsSection/useRefreshShortlistedFlights'
import useViewBookedShortlistedFlight from '../components/ShortlistedFlightsSection/useViewBookedShortlistedFlight'

interface Props {
  visible: boolean
  ViewComponent?: ComponentType<ComponentProps<typeof TripListView>>
}

const EMPTY_OPTIMISTIC_EXCLUSIONS: Record<number, boolean> = {}
const EMPTY_REFRESH_FAILED_FLIGHT_KEYS: Record<string, boolean> = {}
const EMPTY_TRIP_STOPS: Array<TripStop> = []
const ROW_BOOK_READINESS_TIMEOUT_MS = 2000
const MY_ESCAPES_URL = '/account/my-escapes'
const MAX_BULK_DELETE_ITEM_IDS = 100
const MAX_BULK_MOVE_ITEM_IDS = 20
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}/

function createDefaultFilters(): TripListFilters {
  return {
    dates: new Set<string>(),
    types: new Set<TripListRowType>(),
    locations: new Set<string>(),
    statuses: new Set<TripListStatusValue>(),
    bookableOnly: false,
  }
}

function getNextSort(sort: TripListSort | undefined, field: TripListSortField): TripListSort | undefined {
  if (sort?.field !== field) {
    return { field, direction: 'asc' }
  }

  if (sort.direction === 'asc') {
    return { field, direction: 'desc' }
  }

  return undefined
}

function getRowConfirmationCode(row: TripListRow): string | undefined {
  if (row.kind === 'item') {
    const item = row.item
    if (!('confirmationCode' in item)) return undefined
    return typeof item.confirmationCode === 'string' && item.confirmationCode.length > 0 ? item.confirmationCode : undefined
  }

  const savedItemData = row.option.savedItemData
  if (isLeFlightSavedItemData(savedItemData) && savedItemData.orderId) return savedItemData.orderId
  const returnLegSavedItemData = row.option.returnLeg?.savedItemData
  if (isLeFlightSavedItemData(returnLegSavedItemData) && returnLegSavedItemData.orderId) return returnLegSavedItemData.orderId
  return undefined
}

function getBookingUrl(row: TripListRow): string {
  const confirmationCode = getRowConfirmationCode(row)
  return confirmationCode ? `${MY_ESCAPES_URL}/order/${confirmationCode}` : MY_ESCAPES_URL
}

function getFlightDateParam(value: string | undefined): string | undefined {
  return value?.match(DATE_PARAM_PATTERN)?.[0]
}

function ListContainer({ visible, ViewComponent = TripListView }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const {
    viewBookedFlightDetails,
    viewUnbookedFlightDetails,
  } = useViewBookedShortlistedFlight(showModal)
  const { bookFlight } = useBookShortlistedFlight(showModal)
  const { refresh: refreshShortlistedFlights } = useRefreshShortlistedFlights()
  const { chatId, isStreaming, isStreamOpen } = useChatStreamStatus()
  const { luxPlusEnabled, onLuxPlusToggle } = useTripListPricingContext()
  const {
    itineraryData,
    multiCartId,
    scrollTarget,
    clearScrollTarget,
    deleteManualItineraryTarget,
    moveManualItineraryItems,
    isManualItineraryDeletePending,
    isManualItineraryMovePending,
    isFlightCartMutationPending,
  } = useItineraryContext()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const actionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending || isManualItineraryMovePending || isFlightCartMutationPending

  const [sort, setSort] = useState<TripListSort | undefined>(undefined)
  const [filters, setFilters] = useState<TripListFilters>(createDefaultFilters)
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set())
  const [pendingBookRow, setPendingBookRow] = useState<TripListRow | undefined>(undefined)
  const [flightSearchRow, setFlightSearchRow] = useState<TripListTransportRow | undefined>(undefined)
  const flightDetailsRequestRef = useRef(0)
  const visibleItineraryData = visible ? itineraryData : undefined

  useFetchCartPanelOffers(visibleItineraryData, visible)
  const {
    items: cartPanelItems,
    isLoading: cartPanelItemsLoading,
    refetchCartItems,
  } = useCartPanelItems({
    multiCartId,
    itineraryData: visibleItineraryData,
    isLoggedIn: visible && isLoggedIn,
    optimisticExclusions: EMPTY_OPTIMISTIC_EXCLUSIONS,
    refreshFailedFlightKeys: EMPTY_REFRESH_FAILED_FLIGHT_KEYS,
  })
  const { checkoutCartItems } = useTripChatCheckoutActions({ chatId, multiCartId, refetchCartItems })
  const { isUpdating: isNoteUpdating, pendingRowId: noteUpdateRowId, updateNote } = useTripListNoteUpdate(chatId, currentRegionCode)
  const { isUpdating: isStatusUpdating, pendingRowId: statusUpdateRowId, updateStatus } = useTripListStatusUpdate(chatId, currentRegionCode)
  const cartPanelItemsLoadingRef = useRef(cartPanelItemsLoading)

  useEffect(() => {
    cartPanelItemsLoadingRef.current = cartPanelItemsLoading
  }, [cartPanelItemsLoading])

  const baseRows = useMemo(() => buildTripListRows(itineraryData, intl), [intl, itineraryData])
  const allRows = useMemo(
    () => applyCartPanelPricesToTripListRows(baseRows, cartPanelItems, currentCurrency),
    [baseRows, cartPanelItems, currentCurrency],
  )

  useEffect(() => {
    setFilters(prev => {
      const availableDates = new Set(
        allRows
          .map(row => row.day?.date)
          .filter((date): date is string => date !== undefined),
      )
      const dates = new Set([...prev.dates].filter(date => availableDates.has(date)))
      const availableLocations = new Set(
        allRows
          .filter(row => row.kind === 'item')
          .map(row => row.stopLocation.filterValue),
      )
      const locations = new Set([...prev.locations].filter(location => availableLocations.has(location)))
      const availableStatuses = new Set(allRows.map(row => row.status.value))
      const statuses = new Set([...prev.statuses].filter(status => availableStatuses.has(status)))
      if (
        dates.size === prev.dates.size &&
        locations.size === prev.locations.size &&
        statuses.size === prev.statuses.size
      ) return prev
      return { ...prev, dates, locations, statuses }
    })
  }, [allRows])

  const visibleRows = useMemo(() => {
    return sortTripListRows(filterTripListRows(allRows, filters), sort, luxPlusEnabled)
  }, [allRows, filters, luxPlusEnabled, sort])
  const selectedRows = useMemo(
    () => getSelectedTripListRows(visibleRows, selectedRowIds),
    [selectedRowIds, visibleRows],
  )
  const hasActiveFilters = (
    filters.dates.size > 0 ||
    filters.locations.size > 0 ||
    filters.types.size > 0 ||
    filters.statuses.size > 0 ||
    filters.bookableOnly
  )
  const groups = useMemo(() => {
    return groupTripListRows(visibleRows, { sort, grouped: !sort && !hasActiveFilters }, intl)
  }, [hasActiveFilters, intl, sort, visibleRows])

  useEffect(() => {
    setSelectedRowIds(prev => {
      const visibleIds = new Set(visibleRows.map(row => row.id))
      const next = new Set([...prev].filter(id => visibleIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleRows])

  useEffect(() => {
    if (!pendingBookRow) return
    if (!visible || !visibleRows.some(row => row.id === pendingBookRow.id)) {
      setPendingBookRow(undefined)
    }
  }, [pendingBookRow, visible, visibleRows])

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedRowIds(checked ? new Set(visibleRows.map(row => row.id)) : new Set())
  }, [visibleRows])

  const handleSelectRow = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(rowId)
      } else {
        next.delete(rowId)
      }
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedRowIds(new Set())
  }, [])

  const handleDateFiltersChange = useCallback((dates: Set<string>) => {
    setFilters(prev => {
      return { ...prev, dates: new Set(dates) }
    })
  }, [])

  const handleLocationFiltersChange = useCallback((locations: Set<string>) => {
    setFilters(prev => {
      return { ...prev, locations: new Set(locations) }
    })
  }, [])

  const handleTypeFiltersChange = useCallback((types: Set<TripListRowType>, bookableOnly: boolean) => {
    setFilters(prev => {
      return { ...prev, types: new Set(types), bookableOnly }
    })
  }, [])

  const handleStatusFiltersChange = useCallback((statuses: Set<TripListStatusValue>) => {
    setFilters(prev => {
      return { ...prev, statuses: new Set(statuses) }
    })
  }, [])

  const handleSortToggle = useCallback((field: TripListSortField) => {
    setSort(prev => getNextSort(prev, field))
  }, [])

  const handleClearFilters = useCallback(() => {
    setFilters(createDefaultFilters())
    setSort(undefined)
  }, [])

  const openCartPanel = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const closeFlightSearchDrawer = useCallback(() => {
    setFlightSearchRow(undefined)
  }, [])

  const flightSearchDrawerParams = useMemo(() => {
    if (!flightSearchRow) return undefined

    const savedItemData = isFlightSavedItemData(flightSearchRow.option.savedItemData) ? flightSearchRow.option.savedItemData : undefined
    const returnLegSavedItemDataInput = flightSearchRow.option.returnLeg?.savedItemData
    const returnLegSavedItemData = isFlightSavedItemData(returnLegSavedItemDataInput) ? returnLegSavedItemDataInput : undefined
    const parsedSearchParams = isLeFlightSavedItemData(savedItemData)
      ? parseSearchParams(savedItemData.searchParams)
      : undefined
    const firstLeg = parsedSearchParams?.itinerary[0]
    const returnLeg = parsedSearchParams?.itinerary[1]

    return {
      originAirportCode: firstLeg?.departureAirport ?? savedItemData?.departureAirport,
      destinationAirportCode: firstLeg?.arrivalAirport ?? savedItemData?.arrivalAirport,
      departureDate: firstLeg?.departureDate ?? getFlightDateParam(savedItemData?.departureTime),
      returnDate: returnLeg?.departureDate ?? getFlightDateParam(returnLegSavedItemData?.departureTime),
      fareType: flightSearchRow.option.returnLeg ? 'return' : parsedSearchParams?.fareType,
      fareClass: parsedSearchParams?.fareClass,
      occupants: parsedSearchParams?.occupancies[0],
    }
  }, [flightSearchRow])

  const openFlightSearchForRow = useCallback((row: TripListTransportRow) => {
    setFlightSearchRow(row)
  }, [])

  const handleUnbookedFlightDetailsClick = useCallback(async(row: TripListTransportRow) => {
    const requestId = flightDetailsRequestRef.current + 1
    flightDetailsRequestRef.current = requestId
    const refreshedFlights = await refreshShortlistedFlights([row.option])
    if (requestId !== flightDetailsRequestRef.current) return

    const refreshedData = refreshedFlights.find(flight => flight.option.itemId === row.option.itemId)

    const didOpenDetails = await viewUnbookedFlightDetails(
      row.option,
      refreshedData,
      bookFlight,
      undefined,
      { suppressUnavailableMessage: true },
    )
    if (requestId !== flightDetailsRequestRef.current) return

    if (!didOpenDetails) {
      openFlightSearchForRow(row)
    }
  }, [bookFlight, openFlightSearchForRow, refreshShortlistedFlights, viewUnbookedFlightDetails])

  const handleRowClick = useCallback((row: TripListRow) => {
    if (row.viewTarget.kind === 'none') return

    if (row.viewTarget.kind === 'offer') {
      eventBus.dispatch({
        type: 'open_offer_drawer',
        offerId: row.viewTarget.offerId,
        accommodationPropertyId: row.viewTarget.accommodationPropertyId,
        offerType: row.viewTarget.offerType,
        searchParams: row.viewTarget.searchParams,
      })
      return
    }

    if (row.viewTarget.kind === 'place') {
      eventBus.dispatch({
        type: 'open_place_drawer',
        googlePlaceId: row.viewTarget.googlePlaceId,
        placeName: row.viewTarget.placeName,
      })
      return
    }

    if (row.viewTarget.kind === 'flightSearch') {
      if (row.kind === 'transport') {
        openFlightSearchForRow(row)
      }
      return
    }

    if (row.viewTarget.kind === 'flightDetails') {
      if (row.kind === 'transport') {
        if (isBookedFlightOption(row.option)) {
          viewBookedFlightDetails(row.option)
        } else {
          handleUnbookedFlightDetailsClick(row).catch(() => openFlightSearchForRow(row))
        }
      }
      return
    }

    eventBus.dispatch({
      type: 'open_place_search_drawer',
      searchQuery: row.viewTarget.searchQuery,
      placeName: row.viewTarget.placeName,
    })
  }, [eventBus, handleUnbookedFlightDetailsClick, openFlightSearchForRow, viewBookedFlightDetails])

  const routeToCompletion = useCallback((row: TripListRow) => {
    if (row.kind === 'transport') {
      openCartPanel()
      return
    }

    if (row.offerTarget) {
      eventBus.dispatch({
        type: 'open_offer_drawer',
        offerId: row.offerTarget.offerId,
        accommodationPropertyId: row.offerTarget.accommodationPropertyId,
        offerType: row.offerTarget.offerType,
        searchParams: row.offerTarget.searchParams,
        scrollToPricing: row.rowType === 'experience',
        scrollToRoomOptions: row.rowType === 'accommodation',
      })
      return
    }

    handleRowClick(row)
  }, [eventBus, handleRowClick, openCartPanel])

  const handleResolvedBookRow = useCallback((row: TripListRow) => {
    const matchedItem = cartPanelItems.find(item => isTripListRowMatchingCartPanelItem(row, item))
    if (!matchedItem) {
      routeToCompletion(row)
      return
    }

    if (isCartPanelItemCheckoutReady(matchedItem)) {
      checkoutCartItems([matchedItem])
      return
    }

    routeToCompletion(row)
  }, [cartPanelItems, checkoutCartItems, routeToCompletion])

  const handleBookRow = useCallback(async(row: TripListRow) => {
    if (actionsDisabled) return
    rememberTripChatCheckoutReturnView('list')
    if (!isLoggedIn) {
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!loginResult.loggedIn) return
      setPendingBookRow(row)
      return
    }

    if (cartPanelItemsLoading) {
      setPendingBookRow(row)
      return
    }

    handleResolvedBookRow(row)
  }, [actionsDisabled, cartPanelItemsLoading, handleResolvedBookRow, isLoggedIn, showModal])

  useEffect(() => {
    if (!pendingBookRow || !isLoggedIn || cartPanelItemsLoading) return
    const row = pendingBookRow
    setPendingBookRow(undefined)
    handleResolvedBookRow(row)
  }, [cartPanelItemsLoading, handleResolvedBookRow, isLoggedIn, pendingBookRow])

  useEffect(() => {
    if (!pendingBookRow || !isLoggedIn || !cartPanelItemsLoading) return
    const timeout = window.setTimeout(() => {
      if (!cartPanelItemsLoadingRef.current) return
      routeToCompletion(pendingBookRow)
      setPendingBookRow(undefined)
    }, ROW_BOOK_READINESS_TIMEOUT_MS)
    return () => window.clearTimeout(timeout)
  }, [cartPanelItemsLoading, isLoggedIn, pendingBookRow, routeToCompletion])

  const handleBookAll = useCallback(() => {
    if (actionsDisabled) return
    openCartPanel()
  }, [actionsDisabled, openCartPanel])

  const handleViewBooking = useCallback((row: TripListRow) => {
    if (actionsDisabled) return
    window.open(getBookingUrl(row), '_blank', 'noopener,noreferrer')
  }, [actionsDisabled])

  const handleAskAi = useCallback((row: TripListRow, origin?: { x: number; y: number }) => {
    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: row.title,
      context: getTripListRowAskAiContext(row),
      origin,
    })
  }, [eventBus])

  const handleAskAiMove = useCallback((row: TripListRow, origin?: { x: number; y: number }) => {
    const rows = [row]

    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: getTripListRowsMoveAskAiTitle(intl, rows),
      context: getTripListRowsMoveAskAiContext(rows),
      placeholder: getTripListRowsMoveAskAiPlaceholder(intl, rows),
      origin,
    })
  }, [eventBus, intl])

  const handleAskAiSelectedRows = useCallback((origin?: { x: number; y: number }) => {
    if (actionsDisabled || selectedRows.length === 0) return
    if (selectedRows.length === 1 && selectedRows[0]) {
      handleAskAi(selectedRows[0], origin)
      return
    }

    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: getTripListRowsAskAiTitle(intl, selectedRows),
      context: getTripListRowsAskAiContext(selectedRows),
      origin,
    })
  }, [actionsDisabled, eventBus, handleAskAi, intl, selectedRows])

  const handleDelete = useCallback((row: TripListRow) => {
    if (actionsDisabled) return
    if (row.kind === 'transport') {
      deleteManualItineraryTarget({ targetType: 'item', itemId: row.option.itemId })
      return
    }

    deleteManualItineraryTarget({ targetType: 'item', itemId: row.item.id })
  }, [actionsDisabled, deleteManualItineraryTarget])

  const handleMoveToDay = useCallback((row: TripListRow, option: TripListMoveDayOption) => {
    if (actionsDisabled || option.selected || !canMoveTripListRowToAnotherDay(row)) return

    moveManualItineraryItems({
      moves: [{
        itemId: getTripListRowTripItemId(row),
        target: option.target,
      }],
    })
  }, [actionsDisabled, moveManualItineraryItems])

  const handleAskAiMoveSelectedRows = useCallback((origin?: { x: number; y: number }) => {
    if (actionsDisabled || selectedRows.length === 0) return
    if (selectedRows.length === 1 && selectedRows[0]) {
      handleAskAiMove(selectedRows[0], origin)
      return
    }

    eventBus.dispatch({
      type: 'ask_ai_for_list_row',
      title: getTripListRowsMoveAskAiTitle(intl, selectedRows),
      context: getTripListRowsMoveAskAiContext(selectedRows),
      placeholder: getTripListRowsMoveAskAiPlaceholder(intl, selectedRows),
      origin,
    })
  }, [actionsDisabled, eventBus, handleAskAiMove, intl, selectedRows])

  const handleMoveSelectedRowsToDay = useCallback(async(option: TripListMoveDayOption) => {
    if (actionsDisabled || option.selected || selectedRows.length === 0) return
    if (!selectedRows.every(canMoveTripListRowToAnotherDay)) return
    if (selectedRows.length > MAX_BULK_MOVE_ITEM_IDS) {
      showSnackbar(intl.formatMessage(
        {
          id: 'nloZuOC-U8iFPBm5spZ0',
          defaultMessage: 'Select {maxItemCount} or fewer items to move at once.',
          description: 'Error snackbar when too many trip planner list rows are selected for bulk moving',
        },
        { maxItemCount: MAX_BULK_MOVE_ITEM_IDS },
      ), 'critical')
      return
    }

    const moved = await moveManualItineraryItems({
      moves: selectedRows.map(row => ({
        itemId: getTripListRowTripItemId(row),
        target: option.target,
      })),
    })
    if (moved) setSelectedRowIds(new Set())
  }, [actionsDisabled, intl, moveManualItineraryItems, selectedRows])

  const handleDeleteSelectedRows = useCallback(async() => {
    if (actionsDisabled || selectedRows.length === 0) return

    const itemIds = getTripListRowsTripItemIds(selectedRows)
    if (itemIds.length > MAX_BULK_DELETE_ITEM_IDS) {
      showSnackbar(intl.formatMessage({
        id: 'NqT82TiGLwfahEZ0ENCm',
        defaultMessage: 'Select 100 or fewer items to delete at once.',
        description: 'Error snackbar when too many trip planner list rows are selected for bulk deletion',
      }), 'critical')
      return
    }

    const itemNames = selectedRows.map(row => row.title)
    const confirmed = await showModal<boolean>(<ConfirmDeleteTripListItemsModal itemNames={itemNames} />)
    if (!confirmed) return

    await deleteManualItineraryTarget({ targetType: 'items', itemIds })
  }, [actionsDisabled, deleteManualItineraryTarget, intl, selectedRows, showModal])

  const handleUpdateNote = useCallback((row: TripListRow, notes: string | undefined) => {
    if (actionsDisabled || isNoteUpdating || isStatusUpdating) return
    updateNote({
      rowId: row.id,
      itemId: getTripListRowTripItemId(row),
      notes,
    })
  }, [actionsDisabled, isNoteUpdating, isStatusUpdating, updateNote])

  const handleUpdateStatus = useCallback((row: TripListRow, status: TripListStatusValue) => {
    if (actionsDisabled || isNoteUpdating || isStatusUpdating || !row.status.isEditable) return
    updateStatus({
      rowId: row.id,
      itemId: getTripListRowTripItemId(row),
      status,
    })
  }, [actionsDisabled, isNoteUpdating, isStatusUpdating, updateStatus])

  if (!visible) return null

  return (
    <>
      <ViewComponent
        groups={groups}
        tripStops={itineraryData?.stops ?? EMPTY_TRIP_STOPS}
        allRows={allRows}
        visibleRows={visibleRows}
        selectedRows={selectedRows}
        selectedRowIds={selectedRowIds}
        filters={filters}
        sort={sort}
        luxPlusEnabled={luxPlusEnabled}
        onSelectAll={handleSelectAll}
        onSelectRow={handleSelectRow}
        onDateFiltersChange={handleDateFiltersChange}
        onLocationFiltersChange={handleLocationFiltersChange}
        onTypeFiltersChange={handleTypeFiltersChange}
        onStatusFiltersChange={handleStatusFiltersChange}
        onClearFilters={handleClearFilters}
        onSortToggle={handleSortToggle}
        onLuxPlusToggle={onLuxPlusToggle}
        onRowClick={handleRowClick}
        onBookRow={handleBookRow}
        onBookAll={handleBookAll}
        onViewBooking={handleViewBooking}
        onAskAi={handleAskAi}
        onAskAiMove={handleAskAiMove}
        onAskAiSelectedRows={handleAskAiSelectedRows}
        onAskAiMoveSelectedRows={handleAskAiMoveSelectedRows}
        onDelete={handleDelete}
        onMoveToDay={handleMoveToDay}
        onMoveSelectedRowsToDay={handleMoveSelectedRowsToDay}
        onDeleteSelectedRows={handleDeleteSelectedRows}
        onClearSelection={handleClearSelection}
        onUpdateNote={handleUpdateNote}
        onUpdateStatus={handleUpdateStatus}
        bookingRowId={pendingBookRow?.id}
        noteUpdateRowId={noteUpdateRowId}
        statusUpdateRowId={statusUpdateRowId}
        notesDisabled={actionsDisabled || isNoteUpdating || isStatusUpdating}
        statusDisabled={actionsDisabled || isNoteUpdating || isStatusUpdating}
        actionsDisabled={actionsDisabled}
        scrollTarget={scrollTarget}
        onScrollComplete={clearScrollTarget}
      />
      {flightSearchRow && (
        <FlightSearchDrawer
          isOpen
          onClose={closeFlightSearchDrawer}
          originAirportCode={flightSearchDrawerParams?.originAirportCode}
          destinationAirportCode={flightSearchDrawerParams?.destinationAirportCode}
          departureDate={flightSearchDrawerParams?.departureDate}
          returnDate={flightSearchDrawerParams?.returnDate}
          showMultiCity
          fareType={flightSearchDrawerParams?.fareType}
          fareClass={flightSearchDrawerParams?.fareClass}
          occupants={flightSearchDrawerParams?.occupants}
          autoSearch={Boolean(
            flightSearchDrawerParams?.originAirportCode &&
            flightSearchDrawerParams?.destinationAirportCode &&
            flightSearchDrawerParams?.departureDate,
          )}
          onSendMessage={sendMessage}
          existingFlights={flightSearchRow.transport.options}
        />
      )}
    </>
  )
}

export default ListContainer
